import { useMemo } from "react";
import { Chat } from "stream-chat-react";
import "stream-chat-react/dist/css/v2/index.css";
import "Components/Chat/ChatOverrides.css";
import type { ChatTokenData } from "Api/Queries/Chat/useChatTokenQuery";
import { ChatInner } from "Components/Chat/ChatInner";
import { createStreamChatI18n } from "Components/Chat/streamChatTranslations";
import { useLanguage } from "Translations/useLanguage";
import { useIsMobile } from "Hooks/useIsMobile";
import { useChatPushFirstPrompt } from "Hooks/Chat/useChatPushFirstPrompt";
import { useStreamChatClient } from "Hooks/Chat/useStreamChatClient";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";

type Props = {
  chatTokenData: ChatTokenData;
  onDetailViewChange: (isDetail: boolean) => void;
};

export const ChatContent = ({ chatTokenData, onDetailViewChange }: Props) => {
  const { currentLanguage } = useLanguage();
  const isMobile = useIsMobile();

  const i18nInstance = useMemo(
    () => createStreamChatI18n(currentLanguage),
    [currentLanguage],
  );

  // The shared per-persona client (same connection as the unread badge) with a token PROVIDER —
  // a background token refetch never tears the socket down, and expired tokens refresh in place.
  const { client } = useStreamChatClient(chatTokenData);

  // Rendered only past ChatOnboardingGate, so this is the first moment a persona is "in chat":
  // the one place the iOS push permission prompt fires (no-op outside the iOS shell).
  useChatPushFirstPrompt(client);

  if (!client) {
    return <CenteredCircularProgress />;
  }

  return (
    <Chat client={client} i18nInstance={i18nInstance}>
      <ChatInner
        isMobile={isMobile}
        chatTokenData={chatTokenData}
        onDetailViewChange={onDetailViewChange}
      />
    </Chat>
  );
};
